import React, {ReactElement, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import {useStateContext} from "../../../hooks/useStateContext";
import EditableCell from "../../../pages/ServiceProviderPages/components/EditableCell";
import Box from "@mui/material/Box";
import {ExpandMoreRounded} from "@mui/icons-material";

export const ConfirmationTable = ({
                                    item,
                                    editableFields,
                                    requiredFields,
                                    onChange,
                                  }: {
  item: any;
  editableFields?: any;
  requiredFields?: any;
  onChange?: (field: any, value: any) => void;
}): ReactElement => {

  const {isMobile, user} = useStateContext();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<any>({});

  const hasVehicles = user?.service_providers?.some(
    (provider: any) => provider?.vehicles?.length > 0
  );


  const isFieldRequired = (fieldKey: string): boolean => {
    if (!requiredFields) return false;

    // ha tömb
    if (Array.isArray(requiredFields)) {
      return requiredFields.includes(fieldKey);
    }

    // ha objektum: { driver: true, venue: true }
    if (typeof requiredFields === 'object') {
      return Boolean(requiredFields[fieldKey]);
    }

    return false;
  };

  const renderLabel = (label: string, fieldKey?: string) => {
    const required = fieldKey ? isFieldRequired(fieldKey) : false;

    return (
      <Stack alignItems={'flex-start'}>
        <span>{label}</span>
        {required && (
          <Box
            component="span"
            sx={{
              color: '#fff',
              backgroundColor: '#0288d1',
              px: 0.75,
              py: 0.1,
              borderRadius: '8px',
              fontSize: '9px',
              fontWeight: 700,
              lineHeight: 1.6,
            }}
          >
            kitöltése kötelező
          </Box>
        )}
      </Stack>
    );
  };

  const handleFieldChange = (field: string, value: string) => {

    setFormValues((prev: any) => ({
      ...prev,
      [field]: value,
    }));

    if (onChange) {
      onChange(field, value);
    }
  };


  return (
    <Table
      sx={{
        width: '100%'
      }}
      aria-label="simple table"
    >
      <TableBody
        sx={{
          '& tr:last-child td, & tr:last-child th': {
            border: 0
          },
          ' .MuiTableCell-root': {
            padding: '2px',
          }
        }}
      >

        {item?.service_provider?.name && (
          <TableRow>
            <TableCell component="th" scope="row">
              Szolgáltató:
            </TableCell>
            <TableCell sx={{
              fontWeight: 'bold'
            }} dangerouslySetInnerHTML={{__html: item?.service_provider?.name}}></TableCell>
          </TableRow>
        )}

        <TableRow>
          <TableCell component="th" scope="row">
            Típus:
          </TableCell>
          <TableCell sx={{
            fontWeight: 'bold'
          }} dangerouslySetInnerHTML={{__html: item?.formatted?.type}}></TableCell>
        </TableRow>

        {(item?.type === 'airport-transfer' || item?.type === 'transfer') && (
          <>
            <TableRow
            >
              <TableCell component="th" scope="row">
                Szolgáltatás:
              </TableCell>
              <TableCell>{item?.formatted?.service}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell component="th" scope="row">
                Program:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.activity}}
              ></TableCell>
            </TableRow>

            <TableRow>
              <TableCell component="th" scope="row">
                Csoport:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.group}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Létszám:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.size}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Dátum:
              </TableCell>
              <TableCell>
                <Box>
                  {/* a fő dátum (HTML formázott) */}
                  <span
                    dangerouslySetInnerHTML={{ __html: item?.formatted?.date }}
                  />

                  {/* az extra megjegyzés ugyanabban a cellában */}
                  {item?.is_after_midnight && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#951515',
                        mt: 0.5, // egy kis függőleges térköz
                      }}
                    >
                      Technikailag ez még az előző nap (
                      <b>{item?.date_minus_day}</b>)
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Pickup:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.pickup_time}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Kezdés:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.start_time}}
              ></TableCell>
            </TableRow>
            {item?.type === 'airport-transfer' && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Járatszám:
                </TableCell>
                <TableCell
                  dangerouslySetInnerHTML={{__html: item?.formatted?.flight}}></TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell component="th" scope="row">
                Találkozási pont:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.meeting_place}}
              ></TableCell>
            </TableRow>

            {hasVehicles && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Jármű:
                </TableCell>
                <TableCell
                  dangerouslySetInnerHTML={{__html: item?.formatted?.vehicle}}
                ></TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell component="th" scope="row">
                Sofőr:
              </TableCell>
              <EditableCell
                fieldKey={'driver'}
                value={formValues['driver'] ?? item?.formatted?.driver}
                editableFields={editableFields}
                editingField={editingField}
                setEditingField={setEditingField}
                onChange={(field, value) => {
                  handleFieldChange(field, value);
                }}
                row={item}
                type={'driver'}
              />
            </TableRow>

          </>
        )}

        {(item?.type === 'event-staff' || item?.type === 'event-staff-airport-transfer') && (
          <>
            <TableRow>
              <TableCell component="th" scope="row">
                Fellépő:
              </TableCell>

              <EditableCell
                fieldKey={'event_staff'}
                value={formValues['event_staff'] ?? item?.formatted?.event_staff}
                editableFields={editableFields}
                editingField={editingField}
                setEditingField={setEditingField}
                onChange={(field, value) => {
                  handleFieldChange(field, value);
                }}
                row={item}
                type={'event_staff'}
              />

            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Műsor:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.service}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Program:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.activity}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Csoport:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.group}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Létszám:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.size}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Dátum:
              </TableCell>
              <TableCell>
                <Box>
                  {/* a fő dátum (HTML formázott) */}
                  <span
                    dangerouslySetInnerHTML={{ __html: item?.formatted?.date }}
                  />

                  {/* az extra megjegyzés ugyanabban a cellában */}
                  {item?.is_after_midnight && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#951515',
                        mt: 0.5, // egy kis függőleges térköz
                      }}
                    >
                      Technikailag ez még az előző nap (
                      <b>{item?.date_minus_day}</b>)
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Érkezés:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: (item?.formatted?.type === 'event-staff-airport-transfer' ? item?.formatted?.pickup_arrival_time : item?.formatted?.arrival_time)}}
              >

              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Kezdés:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: (item?.type === 'event-staff' ? item?.formatted?.start_time : item?.formatted?.pickup_time)}}
              ></TableCell>
            </TableRow>
          </>
        )}

        {(item?.type === 'default') && (
          <>
            <TableRow>
              <TableCell component="th" scope="row">
                Szolgáltatás:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.service}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Program:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.activity}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Csoport:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.group}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Létszám:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.size}}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Dátum:
              </TableCell>
              <TableCell>
                <Box>
                  {/* a fő dátum (HTML formázott) */}
                  <span
                    dangerouslySetInnerHTML={{ __html: item?.formatted?.date }}
                  />

                  {/* az extra megjegyzés ugyanabban a cellában */}
                  {item?.is_after_midnight && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#951515',
                        mt: 0.5, // egy kis függőleges térköz
                      }}
                    >
                      Technikailag ez még az előző nap (
                      <b>{item?.date_minus_day}</b>)
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Kezdés:
              </TableCell>
              <TableCell
                dangerouslySetInnerHTML={{__html: item?.formatted?.start_time}}
              ></TableCell>
            </TableRow>
          </>
        )}

        <TableRow>
          <TableCell component="th" scope="row">
            Helyszín:
          </TableCell>
          {/* <TableCell
            dangerouslySetInnerHTML={{__html:(item?.type === 'event-staff-airport-transfer' ? item?.formatted?.meeting_place : item?.formatted?.venue)}}
          >

          </TableCell>*/}

          <EditableCell
            fieldKey={(item?.type === 'event-staff-airport-transfer' ? 'meeting_place' : 'venue')}
            value={
              item?.type === 'event-staff-airport-transfer'
                ? formValues['meeting_place'] ?? item?.formatted?.meeting_place
                : formValues['venue'] ?? item?.formatted?.venue
            }
            editableFields={editableFields}
            editingField={editingField}
            setEditingField={setEditingField}
            onChange={(field, value) => {
              handleFieldChange(field, value);
            }}
            row={item}
            type={(item?.type === 'event-staff-airport-transfer' ? 'meeting_place' : 'venue')}
          />
        </TableRow>
        <TableRow>
          <TableCell component="th" scope="row">
            Guide:
          </TableCell>
          <TableCell
            dangerouslySetInnerHTML={{__html: item?.formatted?.guide}}
          ></TableCell>
        </TableRow>
        <TableRow>
          <TableCell component="th" scope="row">
            Megjegyzés:
          </TableCell>
          <TableCell
            dangerouslySetInnerHTML={{__html: item?.formatted?.notes}}
          ></TableCell>
        </TableRow>

        {item?.message_to_provider && (
          <TableRow>
            <TableCell component="th" scope="row">
              Travel City Megjegyzés:
            </TableCell>
            <TableCell>
              {item?.message_to_provider}
            </TableCell>
          </TableRow>
        )}

        {item.formatted?.custom_fields && item.formatted.custom_fields.map((customField: any) => {
          return (
            <TableRow>
              <TableCell component="th" scope="row">
                {renderLabel(`${customField?.column?.name}:`, customField?.column?.column_name)}
              </TableCell>

              <EditableCell
                fieldKey={customField?.column?.column_name}
                value={formValues[customField?.column?.column_name] ?? (customField?.value?.name ? customField?.value?.name : customField?.value)}
                editableFields={editableFields}
                editingField={editingField}
                setEditingField={setEditingField}
                onChange={(field, value) => {
                  handleFieldChange(field, value);
                }}
                row={item}
                type={customField?.column?.type}
              />


            </TableRow>
          );

        })}

        {item?.formatted?.includes && (
          <TableRow>
            <TableCell colSpan={2} sx={{ padding: '2px !important' }}>
              <Accordion sx={{ background: '#f2f2f2', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                  <Typography variant={'body2'}>Includes</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box dangerouslySetInnerHTML={{ __html: item?.formatted?.includes }} />
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
        )}

      </TableBody>
    </Table>
  );

}